"use client"

import { useEffect, useState } from "react"
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom"
import HomePage from "./components/HomePage"
import LoginPage from "./components/LoginPage"
import WishlistsPage from "./components/WishlistsPage"
import VillaDetailPage from "./components/VillaDetailPage"
import AdminDashboard from "./components/AdminDashboard"
import HotelsPage from "./components/HotelsPage"
import UsersPage from "./components/UsersPage"
import AdminPage from "./components/AdminPage"
import ProfilePage from "./components/ProfilePage"
import { AuthProvider } from "./hooks/useAuth"
import ForOwnerPage from "./components/ForOwner"
import AddPropertyPage from "./components/AddPropertyPage"
import EditPropertyPage from "./components/EditPropertyPage"
import { ProtectedRoute } from "./components/ProtectedRoute"
import OwnerPage from "./components/OwnerPage"
import ProtectedOwnerRoute from "./components/ProtectedOwnerRoute"
import OwnerReviews from "./components/owner-reviews"

import { Toaster } from "./components/ui/toaster"
import AdminQueries from "./components/AdminQueries"
import PropertyAnalytics from "./components/PropertyAnalytics"

import OwnerManagement from "./components/Ownermanagement"
import ExplorePage from "./components/ExplorePage"
import DestinationPage from "./components/DestinationPage"
import "./styles/datepicker.css"
import MobileImageGallery from "./components/MobileImageGallery"
import ReviewsList from "./components/ReviewsList"
import PromotedVillas from "./components/PromotedVillas"

import { OwnerBookingPage } from "./components/OwnerBookingPage"
import { UserBookingPage } from "./components/UserBookingPage"
import CancellationRequestsPage from "./components/CancellationRequestsPage"
import { AdminBookingPage } from "./components/adminbookingpage"
import { BillingPage } from "./components/BillingPage"
import OffersDiscountPage from "./components/offers&discountpage"
import SpecialOccasionsPage from "./components/SpecialOccasionsPage"
import UserOffersPage from "./components/UserOffersPage"
import TermsConditionsPage from "./pages/TermsConditions"
import PrivacyPolicyPage from "./pages/PrivacyPolicy"
import AboutUsPage from "./pages/Aboutus"
import ContactUsPage from "./pages/Contactus"
import MealImageGallery from "./components/MealImageGallery"
import UsersQueryPage from "./components/UsersQueryPage"
import AuthListener from "./hooks/AuthListener"
import LuxuryVillas from "./components/LuxuryVillas"
import DestinationsPage from "./components/DestinationsPage"
import BlogCreationPage from "./components/BlogCreationPage"
import NavigationLoginHandler from "./components/NavigationLoginHandler"
import AdminVillaImageManager from "./components/AdminVillaImageManager"
import AdminAvailabilityPage from "./components/AdminAvailabilityPage"
import SearchResults from "./components/SearchResults"
import { HelmetProvider } from 'react-helmet-async';
import { ClerkProvider } from '@clerk/clerk-react';
import { CLERK_PUBLISHABLE_KEY, HAS_CLERK_PUBLISHABLE_KEY } from './lib/config';
import { useAuth } from "./hooks/useAuth"


function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  const [cachedProperties, setCachedProperties] = useState([])

  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth"

    return () => {
      document.documentElement.style.scrollBehavior = "auto"
    }
  }, [])

  const updateCachedProperties = (properties) => {
    setCachedProperties(properties)
  }

  const appTree = (
    <HelmetProvider>
      <Router>
        <AuthProvider>
          <ScrollToTop />
          <AuthListener />
          <NavigationLoginHandler />
          <Routes>
              <Route
                path="/"
                element={<HomePage cachedProperties={cachedProperties} updateCachedProperties={updateCachedProperties} />}
              />
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute adminOnly>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/hotels"
                element={
                  <ProtectedRoute adminOnly>
                    <HotelsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/create-blog"
                element={
                  <ProtectedRoute adminOnly>
                    <BlogCreationPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/villa-images"
                element={
                  <ProtectedRoute adminOnly>
                    <AdminVillaImageManager />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute adminOnly>
                    <UsersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/admins"
                element={
                  <ProtectedRoute adminOnly>
                    <AdminPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/luxury-villas"
                element={
                  <ProtectedRoute adminOnly>
                    <LuxuryVillas />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/analytics"
                element={
                  <ProtectedRoute adminOnly>
                    <PropertyAnalytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/queries"
                element={
                  <ProtectedRoute adminOnly>
                    <AdminQueries />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/availability"
                element={
                  <ProtectedRoute adminOnly>
                    <AdminAvailabilityPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users-queries"
                element={
                  <ProtectedRoute adminOnly>
                    <UsersQueryPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/promoted-villas"
                element={
                  <ProtectedRoute adminOnly>
                    <PromotedVillas />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/offers-discounts"
                element={
                  <ProtectedRoute adminOnly>
                    <OffersDiscountPage
                      cachedProperties={cachedProperties}
                      updateCachedProperties={updateCachedProperties}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/bookings"
                element={
                  <ProtectedRoute adminOnly>
                    <AdminBookingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/billing"
                element={
                  <ProtectedRoute adminOnly>
                    <BillingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/special-occasions"
                element={
                  <ProtectedRoute adminOnly>
                    <SpecialOccasionsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/destinations"
                element={
                  <ProtectedRoute adminOnly>
                    <DestinationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/wishlists"
                element={
                  <ProtectedRoute>
                    <WishlistsPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/villa/:villaId/*" element={<VillaDetailPage />} />
              <Route path="/villa/:villaId/photos" element={<MobileImageGallery />} />
              <Route path="/reviews/:villaId" element={<ReviewsList />} />
              <Route path="/for-owner" element={<ForOwnerPage />} />
              <Route
                path="/add-property"
                element={
                  <ProtectedRoute>
                    <AddPropertyPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/edit-property/:id"
                element={
                  <ProtectedRoute>
                    <EditPropertyPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/owner"
                element={
                  <ProtectedOwnerRoute>
                    <OwnerPage />
                  </ProtectedOwnerRoute>
                }
              />

              <Route
                path="/owner/reviews"
                element={
                  <ProtectedOwnerRoute>
                    <OwnerReviews />
                  </ProtectedOwnerRoute>
                }
              />

              <Route
                path="/owner/management"
                element={
                  <ProtectedOwnerRoute>
                    <OwnerManagement />
                  </ProtectedOwnerRoute>
                }
              />
              <Route
                path="/explore"
                element={
                  <ExplorePage cachedProperties={cachedProperties} updateCachedProperties={updateCachedProperties} />
                }
              />

              <Route
                path="/owner/bookings"
                element={
                  <ProtectedOwnerRoute>
                    <OwnerBookingPage />
                  </ProtectedOwnerRoute>
                }
              />
              <Route
                path="/bookings"
                element={
                  <ProtectedRoute>
                    <UserBookingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cancellation-requests"
                element={
                  <ProtectedOwnerRoute>
                    <CancellationRequestsPage />
                  </ProtectedOwnerRoute>
                }
              />
              <Route path="/offers" element={<UserOffersPage />} />
              <Route path="/terms" element={<TermsConditionsPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/about" element={<AboutUsPage />} />
              <Route path="/contact" element={<ContactUsPage />} />
              <Route path="/meal-gallery" element={<MealImageGallery />} />
              <Route path="/search" element={<SearchResults />} />

              {/* Destination routes - these will handle /villas-in-cityname URLs */}
              <Route path="/villas-in-:destination" element={<DestinationPage />} />

              {/* Specific destination pages - you can create these for better SEO */}
              <Route path="/villas-in-lonavala" element={<DestinationPage cityName="Lonavala" />} />
              <Route path="/villas-in-karjat" element={<DestinationPage cityName="Karjat" />} />
              <Route path="/villas-in-neral" element={<DestinationPage cityName="Neral" />} />
              <Route path="/villas-in-alibag" element={<DestinationPage cityName="Alibag" />} />
              <Route path="/villas-in-igatpuri" element={<DestinationPage cityName="Igatpuri" />} />
              <Route path="/villas-in-panchgani" element={<DestinationPage cityName="Panchgani" />} />
            </Routes>
        </AuthProvider>
        <Toaster />
      </Router>
    </HelmetProvider>
  )

  if (!HAS_CLERK_PUBLISHABLE_KEY) {
    return appTree
  }

  return (
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      appearance={{
        variables: {
          colorPrimary: '#000000',
          borderRadius: '12px',
          fontFamily: 'Poppins, system-ui, sans-serif',
        },
        elements: {
          formButtonPrimary: 'bg-black hover:bg-gray-800 text-white rounded-full',
          card: 'rounded-2xl shadow-2xl',
          headerTitle: 'text-xl font-bold',
          headerSubtitle: 'text-gray-500',
          socialButtonsBlockButton: 'rounded-full border',
          footer: 'hidden',
        },
        layout: {
          logoImageUrl: '/VillaKosh.png',
          logoPlacement: 'inside',
          showOptionalFields: false,
        },
      }}
    >
      {appTree}
    </ClerkProvider>
  )
}

export default App
